import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import DevicesOtherOutlinedIcon from '@mui/icons-material/DevicesOtherOutlined';
import SettingsRemoteOutlinedIcon from '@mui/icons-material/SettingsRemoteOutlined';
import BackupOutlinedIcon from '@mui/icons-material/BackupOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import type { SvgIconComponent } from '@mui/icons-material';

/**
 * Maturity, told honestly (differentiator #4 — "we show you the real build status").
 * - live      → built, wired, and running in our own MSP practice today.
 * - configure → built and wired, runs on sample data until an admin connects the
 *               integration/credentials (the product's simulation-until-configured pattern).
 * - building  → actively under construction.
 * - roadmap   → a committed direction, not yet built.
 * Keep these matched to reality — never label something 'live'/'configure' that isn't wired.
 */
export type FeatureStatus = 'live' | 'configure' | 'building' | 'roadmap';

export const STATUS_META: Record<FeatureStatus, { grid: string; detail: string }> = {
  live: { grid: 'Live in our practice', detail: 'Running in our own MSP practice' },
  configure: { grid: 'Available on configure', detail: 'Available — sample data until connected' },
  building: { grid: 'Building now', detail: 'In development' },
  roadmap: { grid: 'On the roadmap', detail: 'Planned' },
};

export type Feature = {
  slug: string;
  icon: SvgIconComponent;
  illustration: 'tickets' | 'devices' | 'network' | 'backup' | 'security' | 'crm' | 'tenant' | 'compliance' | 'portal';
  title: string;
  /** Short card copy (home teaser + platform grid). */
  body: string;
  badge?: string;
  /** Longer intro paragraph for the detail page. */
  intro: string;
  capabilities: string[];
  status: FeatureStatus;
};

export const features: Feature[] = [
  {
    slug: 'helpdesk-ticketing',
    icon: ConfirmationNumberOutlinedIcon,
    illustration: 'tickets',
    title: 'Helpdesk & tickets',
    body: 'Email-to-ticket, SLA tracking, and device-linked history — the PSA your team lives in every day, with AI triage and drafting on top.',
    intro:
      'The PSA core your techs open first every morning. Every inbound email becomes a ticket automatically, every ticket carries the requester’s full device and site history, and every SLA clock is visible before it’s a problem — with an AI layer that triages, diagnoses, and drafts a resolution path instead of leaving your team staring at a blank reply box.',
    capabilities: [
      'Email-to-ticket intake with automatic requester and device matching',
      'SLA timers by contract/priority with breach warnings before they happen',
      'Full device and site history attached to every ticket, not just a note field',
      'AI-drafted diagnosis and suggested resolution on every new ticket — the trajectory is autonomous resolution of routine, non-destructive classes (see the AI layer)',
      'Merge, split, and escalate across a multi-tenant queue without losing thread history',
    ],
    status: 'live',
  },
  {
    slug: 'asset-device-management',
    icon: DevicesOtherOutlinedIcon,
    illustration: 'devices',
    title: 'Asset & device management',
    body: 'Track every laptop, desktop, and mobile device through check-out, check-in, lifecycle, and warranty.',
    intro:
      'A single source of truth for every device you’re responsible for — from the day it’s ordered to the day it’s retired. Check-out and check-in with a real person attached, warranty and lifecycle dates that surface themselves instead of living in a spreadsheet, and a device record that every ticket, every audit, and every QBR can point back to.',
    capabilities: [
      'Full device lifecycle: intake, assignment, check-out/check-in, retirement',
      'Warranty and end-of-life tracking that surfaces upcoming expirations proactively',
      'Per-site and per-client inventory rollups for budgeting and audits',
      'Device history linked to every ticket, alert, and monitoring event it was ever part of',
      'Bulk import/export for onboarding an existing fleet without re-keying everything by hand',
    ],
    status: 'live',
  },
  {
    slug: 'endpoint-management-rmm',
    icon: SettingsRemoteOutlinedIcon,
    illustration: 'network',
    title: 'Endpoint management & RMM (UEM)',
    body: 'A native outbound-only agent for Windows and Linux — signed remote jobs, patch deployment, and remote control — plus Jamf, Intune, NinjaOne, and Action1 in one console.',
    badge: 'No inbound site-firewall exposure',
    intro:
      'Unified endpoint management that never asks you to open a port. Nexus runs its own cross-platform agent (Windows and Linux) that phones home outbound-only, backed by an Ed25519 signed-job engine and a default-deny capability model — so remote work is authorized cryptographically, not trusted implicitly. Patch deployment is live end-to-end on real hardware today; remote scripting, remote control, and every major MDM/RMM you already run are folded into the same console instead of a separate pane of glass per vendor.',
    capabilities: [
      'Native outbound-only agent for Windows and Linux — zero inbound firewall rules at any client site',
      'Ed25519 signed remote jobs with a default-deny capability registry — nothing runs on a device unless it’s cryptographically authorized',
      'Patch deployment with persisted deployment rings, maintenance windows, and auto-halt (live end-to-end on real hardware)',
      'Governed endpoint operations — inspect and control services, query and terminate processes, look up logged-in sessions, export diagnostics, reboot or shut down with a bounded delay; every one of these was individually approved before it could be issued at all',
      'A ~500-script PowerShell library that imports as tenant-scoped unsigned drafts with immutable provenance — nothing becomes runnable on an endpoint just by being imported',
      'Remote scripting and embedded remote control (MeshCentral) launched straight from the device or a ticket',
      'One console over Jamf, Intune, NinjaOne, and Action1 — plus FortiGate, Omada, Meraki, and UniFi network monitoring',
    ],
    status: 'live',
  },
  {
    slug: 'm365-backup',
    icon: BackupOutlinedIcon,
    illustration: 'backup',
    title: 'Microsoft 365 backup',
    body: 'Our own M365 backup — Exchange, OneDrive, and SharePoint into your own storage bucket, verified by hash, with per-item restore.',
    badge: 'Your bucket, your keys',
    intro:
      'A backup product built into the platform, not a third-party line item bolted onto your stack. Nexus walks your Microsoft 365 tenant through the Graph API — Exchange Online mailboxes, OneDrive drive trees, and SharePoint document libraries — and writes them to an S3-compatible bucket you own (Wasabi, AWS S3, or Cloudflare R2), not ours. Every object is hashed and verify-checked, retention prunes on your schedule, and restore is per-item, not all-or-nothing.',
    capabilities: [
      'Exchange Online, OneDrive, and SharePoint backup via the Microsoft Graph API',
      'Writes to your own S3-compatible bucket — Wasabi, AWS S3, or Cloudflare R2 — with keys encrypted at rest',
      'SHA-256 verification on every object and a verify-by-rehash pass, so a backup is proven, not assumed',
      'Retention pruning and run history on a per-tenant schedule',
      'Per-item restore and signed download — recover one mailbox or file, not the whole tenant',
    ],
    status: 'configure',
  },
  {
    slug: 'security-suite',
    icon: ShieldOutlinedIcon,
    illustration: 'security',
    title: 'Security suite',
    body: 'Credential vault with rotation, phishing simulation, breach monitoring, and AI-prioritized vulnerability scanning.',
    intro:
      'The security layer built into the platform, not bolted on. An encrypted credential vault with rotation, phishing simulation campaigns you can run against your own users, breach monitoring that watches for exposed credentials, and vulnerability scanning where AI does the first pass of prioritization so your team spends time on what actually matters.',
    capabilities: [
      'Encrypted credential vault with scheduled rotation and access logging',
      'Phishing simulation campaigns with per-user results and coaching follow-up',
      'Breach and dark-web exposure monitoring tied to managed accounts and domains',
      'AI-prioritized vulnerability scanning — severity ranked by real exploitability, not raw CVSS alone',
      'Every finding lands as a ticket with an owner and an SLA, not a report nobody reads',
    ],
    status: 'live',
  },
  {
    slug: 'crm-sales',
    icon: TrendingUpOutlinedIcon,
    illustration: 'crm',
    title: 'CRM & sales',
    body: 'Leads, companies, contacts, and a weighted deal pipeline — where a won deal turns itself into an onboarded, provisioned client.',
    intro:
      'The front of the business, wired to the back. Nexus carries a full CRM — leads, companies, contacts, deals, and campaigns — on a kanban pipeline with staleness bars and a weighted forecast. The part point-tool CRMs can’t do: when a deal is marked won, Nexus turns it into an onboarding project, a contract, and a provisioned tenant automatically — because the CRM and the platform that delivers the work are the same system.',
    capabilities: [
      'Leads, companies, contacts, deals, and campaigns in one tenant-isolated CRM',
      'Kanban pipeline with staleness indicators and a best/worst weighted forecast',
      'Won-deal conversion straight into an onboarding project, a contract, and tenant provisioning',
      'Templated outreach email that sends for real and honors opt-out',
      'Lead intake from the marketing site and campaign ROI reporting',
      'An AI growth layer on top — deterministic scoring, sequences, funnels, and a governed autonomous SDR (see AI sales & marketing)',
    ],
    status: 'live',
  },
  {
    slug: 'ai-sales-marketing',
    icon: CampaignOutlinedIcon,
    illustration: 'crm',
    title: 'AI sales & marketing',
    body: 'Lead scoring, multi-step sequences, funnels with drop-off analytics, and an autonomous SDR that only sends inside guardrails the server enforces.',
    badge: 'The one place AI sends on its own',
    intro:
      'The growth layer on top of the CRM — and the first place in Nexus where an AI agent is allowed to send something without a human clicking approve first. That is a deliberate exception, not a loosening of the rule: outreach is reversible, low-blast-radius, and easy to bound, so it is where supervised autonomy earns its keep. The bounds are enforced at the server, not in the interface — an owner kill switch, contact opt-out, a suppression list, and a per-tenant daily rate limit sit between the agent and the outside world, and every autonomous send is written to an immutable audit trail.',
    capabilities: [
      'AI Sales Copilot — lead and deal scoring computed deterministically (the numbers never come from the model), next-best-action, and drafts-only email composition with merge-field preview',
      'Read-only natural-language querying over your own CRM aggregates, plus signal-weighted forecast bands',
      'Multi-step email sequences with enrollment tracking and a suppression list enforced at both enroll and send',
      'Funnels and landing pages with per-step drop-off analytics, fed by real public capture — publishing stays a manual admin action',
      'Autonomous SDR bounded by an owner kill switch, opt-out, suppression, and a per-tenant daily rate limit, with an immutable audit record for every send — plus a signed inbound path that feeds prospect replies back into the thread without sending anything',
      'Advisory, fill-empty-only enrichment through a provider waterfall — it links a lead to an existing company but never invents one, and a human’s typed value always wins',
      'Campaign attribution that survives the lead-to-deal boundary, with every report stating what it could not attribute instead of quietly dropping it',
    ],
    status: 'configure',
  },
  {
    slug: 'tenant-management',
    icon: AccountTreeOutlinedIcon,
    illustration: 'tenant',
    title: 'Unified tenant management',
    body: 'Run every client from one HQ console — cross-tenant boards, hard data isolation, and secure switch-into-tenant support.',
    intro:
      'One operations console over every organization you manage. HQ rolls up tickets, projects, alerts, and devices across all tenants into a single set of boards, while each tenant stays hard-isolated — its own data (enforced at the database with row-level security), its own encrypted secrets, its own backups, its own job cadence. Provision a new client and its first admin in one step, and switch into any tenant for support through an audited, mirrored session instead of sharing logins.',
    capabilities: [
      'HQ operations console with cross-tenant boards for tickets, projects, alerts, and devices',
      'Hard per-tenant isolation — Postgres row-level security, per-tenant encrypted secrets and backups',
      'One-step tenant provisioning with a first-admin account and one-time password reveal',
      'Secure switch-into-tenant impersonation through an audited, mirrored session',
      'Resolution by header, cookie, path, subdomain, or custom domain — one platform, many front doors',
    ],
    status: 'live',
  },
  {
    slug: 'compliance-qbr',
    icon: FactCheckOutlinedIcon,
    illustration: 'compliance',
    title: 'Compliance & QBR',
    body: 'Live posture tracking against the frameworks you answer to, rolled up into an automated, board-ready quarterly review.',
    intro:
      'Compliance as a living dashboard, not a once-a-year fire drill. Posture tracking maps to the frameworks you actually answer to — NIST CSF, SOC 2, HIPAA, PCI-DSS, ISO 27001, CIS, and FERPA/COPPA — and rolls up automatically into a QBR deck your team doesn’t have to build from scratch every quarter.',
    capabilities: [
      'Control-posture tracking mapped across NIST CSF, SOC 2, HIPAA, PCI-DSS, ISO 27001, CIS, and FERPA/COPPA',
      'Automated, board-ready Quarterly Business Review generation — customizable, on-demand periods too',
      'Evidence and artifact tracking tied directly to each control, not a separate binder',
      'Gap reporting that shows exactly what’s implemented, partial, or planned — no guessing',
      'Framework coverage grows with the platform; nothing is marked done until the real control is wired',
    ],
    status: 'live',
  },
  {
    slug: 'client-staff-portal',
    icon: GroupsOutlinedIcon,
    illustration: 'portal',
    title: 'Client & staff portal',
    body: 'Client and staff access with electronic agreement signing and announcements, out of the box.',
    intro:
      'A branded front door for the people you serve. Clients see their own tickets, devices, and announcements without emailing your team to ask; staff get a self-service portal for the same. Agreement templates ship with electronic signing built in, so onboarding a new client or a new hire doesn’t require a separate e-sign tool.',
    capabilities: [
      'Client-facing portal: tickets, device inventory, and announcements, scoped per tenant',
      'Staff portal for internal self-service, separate from the client-facing view',
      'Electronic signing on agreement templates — no bolt-on DocuSign-style tool required',
      'Announcements and broadcast messaging to a client, a site, or everyone',
      'Every portal action is tenant-isolated by design — one client never sees another’s data',
    ],
    status: 'live',
  },
];

export function getFeatureBySlug(slug: string | undefined): Feature | undefined {
  return features.find((f) => f.slug === slug);
}
